import type { ErrorVariant, SidekickError } from './chatTypes';
import { SYNTHETIC_TRANSPORT_ERROR_CODE } from './constants';

/**
 * Where the orchestrator surfaces a classified error. `toast` is the
 * transient network path (`onNetworkError`); `inline` is an
 * {@link ErrorEvent} + {@link AgentErrorCard}.
 */
export type SidekickErrorSurface = 'inline' | 'toast';

/**
 * UI treatment derived from a BE `error.code`. `variant` drives the
 * body copy in `AgentErrorCard`; `canRetry` drives whether the Retry
 * button renders enabled (`true`) or in the permanently-disabled grey
 * state per Figma node `15612:63773` (`false`).
 */
export type ErrorClassification = {
  variant: ErrorVariant;
  canRetry: boolean;
};

/**
 * Full presentation contract for one `SidekickError`: copy bucket,
 * retry affordance, surface routing, and optional rate-limit countdown
 * metadata from the spec envelope.
 */
export type ErrorPresentation = ErrorClassification & {
  surface: SidekickErrorSurface;
  retryAfterSeconds?: number;
};

/**
 * Per-code defaults when the BE omits `retryable` on the wire envelope.
 * `surface` is always `inline` for spec codes — only the synthetic
 * {@link SYNTHETIC_TRANSPORT_ERROR_CODE} routes to `toast`.
 */
type SpecCodeDefaults = ErrorClassification & { surface: 'inline' };

/**
 * Source of truth for mapping spec `error.code` → FE UI treatment.
 * Keys match {@link SIDEKICK_SPEC_ERROR_CODES} in `sse/sidekickHttp.ts`.
 * Unknown codes fall through to `SIDEKICK_ERROR_FALLBACK`.
 */
export const SIDEKICK_ERROR_CODE_MAP: Record<string, SpecCodeDefaults> = {
  INVALID_REQUEST: {
    variant: 'unclassified',
    canRetry: false,
    surface: 'inline',
  },
  INVALID_QUERY: {
    variant: 'unclassified',
    canRetry: false,
    surface: 'inline',
  },
  OUT_OF_DOMAIN: {
    variant: 'cannotAnswer',
    canRetry: false,
    surface: 'inline',
  },
  UNSAFE_QUERY: { variant: 'cannotAnswer', canRetry: false, surface: 'inline' },
  UNAUTHORIZED: { variant: 'unclassified', canRetry: false, surface: 'inline' },
  FORBIDDEN: { variant: 'unclassified', canRetry: false, surface: 'inline' },
  NOT_FOUND: { variant: 'unclassified', canRetry: false, surface: 'inline' },
  RATE_LIMITED: { variant: 'temporary', canRetry: false, surface: 'inline' },
  INTERNAL: { variant: 'temporary', canRetry: true, surface: 'inline' },
  SQL_EXECUTION: { variant: 'temporary', canRetry: true, surface: 'inline' },
  RAG_RETRIEVAL: { variant: 'temporary', canRetry: true, surface: 'inline' },
  LLM_ERROR: { variant: 'temporary', canRetry: true, surface: 'inline' },
  SERVICE_UNAVAILABLE: {
    variant: 'temporary',
    canRetry: true,
    surface: 'inline',
  },
  TIMEOUT: { variant: 'temporary', canRetry: true, surface: 'inline' },
  // Also HTTP 403, but a different failure from FORBIDDEN: the
  // organization has no Sidekick credentials provisioned, so no grant
  // the caller could hold would let the request through.
  ORGANIZATION_NOT_ONBOARDED: {
    variant: 'notOnboarded',
    canRetry: false,
    surface: 'inline',
  },
};

/**
 * Defensive default for any `error.code` the BE returns that this table
 * does not know about. Favors `canRetry: true` so the user is never
 * silently stuck without a retry affordance on a transient failure.
 */
export const SIDEKICK_ERROR_FALLBACK: ErrorPresentation = {
  variant: 'unclassified',
  canRetry: true,
  surface: 'inline',
};

const toPresentation = (
  base: SpecCodeDefaults | ErrorPresentation,
  error: SidekickError
): ErrorPresentation => ({
  variant: base.variant,
  canRetry:
    typeof error.retryable === 'boolean' ? error.retryable : base.canRetry,
  surface: base.surface,
  ...(typeof error.retryAfterSeconds === 'number'
    ? { retryAfterSeconds: error.retryAfterSeconds }
    : {}),
});

/**
 * Resolves a full {@link SidekickError} into presentation metadata for
 * the orchestrator and inline card builder. Honors the spec `retryable`
 * flag when present; otherwise uses per-code defaults above.
 * Preserves `retryAfterSeconds` for rate-limit UX follow-ups.
 */
export const classifySidekickErrorPresentation = (
  error: SidekickError
): ErrorPresentation => {
  if (error.code === SYNTHETIC_TRANSPORT_ERROR_CODE) {
    return {
      variant: 'unclassified',
      canRetry: false,
      surface: 'toast',
      ...(typeof error.retryAfterSeconds === 'number'
        ? { retryAfterSeconds: error.retryAfterSeconds }
        : {}),
    };
  }
  const base = SIDEKICK_ERROR_CODE_MAP[error.code] ?? SIDEKICK_ERROR_FALLBACK;
  return toPresentation(base, error);
};

/**
 * Resolves a BE `error.code` string into the matching
 * `ErrorClassification`, falling back to `SIDEKICK_ERROR_FALLBACK` for
 * unknown codes. Prefer {@link classifySidekickErrorPresentation} when
 * the full envelope (incl. `retryable`) is available.
 */
export const classifySidekickError = (code: string): ErrorClassification => {
  const { variant, canRetry } = classifySidekickErrorPresentation({
    code,
    message: '',
  });
  return { variant, canRetry };
};
